import { useState, type FormEvent } from 'react';
import {
  basispuntenNaarProcent,
  formatteerEuro,
  parseEuroNaarCenten,
  parseTariefNaarTiendeCenten,
  procentNaarBasispunten,
  toonTarief,
} from '@qc/core';
import type { Evenement } from '@qc/db';
import { Knop } from '@qc/ui';
import { Veld } from '../auth/Veld.js';
import { usePrijsmodel, velden, type Prijsmodelvelden } from './usePrijsmodel.js';

export interface PrijsmodelProps {
  evenement: Evenement;
  onOpgeslagen: () => Promise<void>;
}

/**
 * Het prijsmodel per evenement. Alle knoppen zijn los aan te zetten, ook als je
 * er maar één gebruikt (deel 4, hoofdstuk 3).
 *
 * De inkooptarieven staan er bewust naast de doorbelasting: wijzigt je
 * betaalpartij zijn tarief, dan pas je hier één getal aan en klopt je marge
 * weer. Dat is de hele bedoeling.
 */
export function Prijsmodel({ evenement, onOpgeslagen }: PrijsmodelProps) {
  const huidig = velden(evenement);
  const opslag = usePrijsmodel();

  const [dagtarief, setDagtarief] = useState(
    formatteerEuro(huidig.dagtariefKraamsetCent, { metSymbool: false }),
  );
  const [perBon, setPerBon] = useState(toonTarief(huidig.doorbelastPerBonTiendeCent));
  const [overOmzet, setOverOmzet] = useState(basispuntenNaarProcent(huidig.doorbelastOverOmzetBp));
  const [inkoopPerBon, setInkoopPerBon] = useState(toonTarief(huidig.inkoopPerBonTiendeCent));
  const [inkoopOverOmzet, setInkoopOverOmzet] = useState(
    basispuntenNaarProcent(huidig.inkoopOverOmzetBp),
  );
  const [eigenKosten, setEigenKosten] = useState(
    formatteerEuro(huidig.eigenKostenCent, { metSymbool: false }),
  );
  const [bufferProcent, setBufferProcent] = useState(basispuntenNaarProcent(huidig.bufferBp));
  const [bufferDagen, setBufferDagen] = useState(String(huidig.bufferDagen));
  const [fout, setFout] = useState<string | null>(null);
  const [gelukt, setGelukt] = useState(false);

  async function bewaren(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault();
    setFout(null);
    setGelukt(false);

    let waarden: Prijsmodelvelden;
    try {
      waarden = {
        dagtariefKraamsetCent: parseEuroNaarCenten(dagtarief),
        doorbelastPerBonTiendeCent: parseTariefNaarTiendeCenten(perBon),
        doorbelastOverOmzetBp: procentNaarBasispunten(overOmzet),
        inkoopPerBonTiendeCent: parseTariefNaarTiendeCenten(inkoopPerBon),
        inkoopOverOmzetBp: procentNaarBasispunten(inkoopOverOmzet),
        eigenKostenCent: parseEuroNaarCenten(eigenKosten),
        bufferBp: procentNaarBasispunten(bufferProcent),
        bufferDagen: Number(bufferDagen),
      };
    } catch (oorzaak) {
      setFout(oorzaak instanceof Error ? oorzaak.message : 'Een van de getallen klopt niet.');
      return;
    }

    if (!Number.isSafeInteger(waarden.bufferDagen) || waarden.bufferDagen < 0) {
      setFout('Het aantal bufferdagen is een geheel getal van 0 of meer.');
      return;
    }

    if (await opslag.bewaren(evenement.id, waarden)) {
      setGelukt(true);
      await onOpgeslagen();
    }
  }

  return (
    <form onSubmit={(e) => void bewaren(e)} className="grid gap-6">
      <section className="rounded-tegel border border-rand bg-vlak p-6">
        <h2 className="mb-1 text-xl font-bold">Wat de organisator betaalt</h2>
        <p className="mb-4 text-tekst-zacht">
          Alle drie los aan te zetten. Gebruik je er maar één, laat de rest op nul.
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          <Veld
            label="Per kraamset per dag"
            value={dagtarief}
            onChange={(e) => setDagtarief(e.target.value)}
            toelichting="In euro's, bijvoorbeeld 35,00."
          />
          <Veld
            label="Doorbelast per bon"
            value={perBon}
            onChange={(e) => setPerBon(e.target.value)}
            toelichting="Vast bedrag per transactie, drie decimalen: 0,066."
          />
          <Veld
            label="Doorbelast over de omzet"
            value={overOmzet}
            onChange={(e) => setOverOmzet(e.target.value)}
            toelichting="In procenten, bijvoorbeeld 1,7."
          />
        </div>
      </section>

      <section className="rounded-tegel border border-rand bg-vlak p-6">
        <h2 className="mb-1 text-xl font-bold">Wat jij aan de betaalpartij betaalt</h2>
        <p className="mb-4 text-tekst-zacht">
          Verandert hun tarief, dan pas je hier één getal aan. Het verschil met de doorbelasting is
          je marge op de transacties.
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          <Veld
            label="Inkoop per bon"
            value={inkoopPerBon}
            onChange={(e) => setInkoopPerBon(e.target.value)}
            toelichting="Wat je zelf per transactie betaalt, bijvoorbeeld 0,066."
          />
          <Veld
            label="Inkoop over de omzet"
            value={inkoopOverOmzet}
            onChange={(e) => setInkoopOverOmzet(e.target.value)}
            toelichting="In procenten, bijvoorbeeld 1,5."
          />
          <Veld
            label="Eigen kosten dit evenement"
            value={eigenKosten}
            onChange={(e) => setEigenKosten(e.target.value)}
            toelichting="Reizen, een dag ter plaatse."
          />
        </div>
      </section>

      <section className="rounded-tegel border border-rand bg-vlak p-6">
        <h2 className="mb-1 text-xl font-bold">Buffer</h2>
        <p className="mb-4 text-tekst-zacht">
          Staat standaard uit. Bij Nederlands pinnen is terugboeken vrijwel uitgesloten.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Veld
            label="Percentage inhouden"
            value={bufferProcent}
            onChange={(e) => setBufferProcent(e.target.value)}
          />
          <Veld
            label="Aantal dagen inhouden"
            value={bufferDagen}
            onChange={(e) => setBufferDagen(e.target.value)}
          />
        </div>
      </section>

      {fout !== null && <p className="text-lg text-fout">{fout}</p>}
      {opslag.fout !== null && <p className="text-lg text-fout">{opslag.fout}</p>}
      {gelukt && <p className="text-lg text-goed">Opgeslagen.</p>}

      <Knop nadruk="hoofd" type="submit" disabled={opslag.bezig}>
        {opslag.bezig ? 'Bezig…' : 'Prijsmodel opslaan'}
      </Knop>
    </form>
  );
}
